import {
  get,
  sortBy,
  isUndefined,
} from 'lodash';
import moment from 'moment';
import { RRule } from 'rrule';
import API from '../../api';
import {
  getDuration,
} from '../../utils/TimeUtil';
import {
  rrule,
} from '../../helpers/DateTime';
import {
  fitItems,
  fitInterrupts,
} from '../../helpers/Event';
import EventModel from '../../models/Event';

function updateRepeatRule(event) {
  let dtstart = get(event, 'repeatRule.options.dtstart', new Date());
  let interval = get(event, 'repeatRule.options.interval', 1);
  let freq = get(event, 'repeatRule.options.freq', 0);
  let byweekday = get(event, 'repeatRule.options.byweekday', []);
  let until = get(event, 'repeatRule.options.until', null);
  const isRepeat = event.repeat;

  if (event.startDate) {
    const startDateUTC = new moment(event.startDate).format();
    dtstart = new Date(startDateUTC);
  }

  if (event.repeatInterval) {
    try {
      interval = parseInt(event.repeatInterval, 10);
    } catch (e) {
      console.error(e);
    }
  }

  if (event.repeatFrequency) {
    if (event.repeatFrequency === 'week') {
      freq = RRule.WEEKLY;
    } else if (event.repeatFrequency === 'month') {
      freq = RRule.MONTHLY;
    } else if (event.repeatFrequency === 'year') {
      freq = RRule.YEARLY;
    }
  }

  if (event.repeatDays) {
    // array to store the unsorted array of days
    const unsortedDays = [];

    event.repeatDays.forEach((day) => {
      if (day === 'Sunday') {
        unsortedDays.push({
          dayIndex: 0,
          weekday: RRule.SU,
        });
      } else if (day === 'Monday') {
        unsortedDays.push({
          dayIndex: 1,
          weekday: RRule.MO,
        });
      } else if (day === 'Tuesday') {
        unsortedDays.push({
          dayIndex: 2,
          weekday: RRule.TU,
        });
      } else if (day === 'Wednesday') {
        unsortedDays.push({
          dayIndex: 3,
          weekday: RRule.WE,
        });
      } else if (day === 'Thursday') {
        unsortedDays.push({
          dayIndex: 4,
          weekday: RRule.TH,
        });
      } else if (day === 'Friday') {
        unsortedDays.push({
          dayIndex: 5,
          weekday: RRule.FR,
        });
      } else if (day === 'Saturday') {
        unsortedDays.push({
          dayIndex: 6,
          weekday: RRule.SA,
        });
      }
    });

    // sort the days of the week for the RRule string ;BYDAY= list of days
    // needed for the Android client to read the RRule properly
    const sortedDays = sortBy(unsortedDays, 'dayIndex');

    byweekday = sortedDays.map(day => day.weekday);
  }

  if (!isUndefined(event.stopDate)) {
    until = event.stopDate;
  }

  const repeatRule = isRepeat
    ? rrule({
      dtstart,
      interval,
      freq,
      byweekday,
      until,
      // enforce Sunday as the first day of the week
      wkst: RRule.SU,
    })
    : rrule({
      dtstart,
    });

  return repeatRule;
}

/**
 * Save an Event
 */
export function saveEvent(event) {
  return async (dispatch, getState) => {
    const {
      login: {
        user,
      },
    } = getState();

    const locationData = user.getLocations({
      locations: event.locations,
      allLocations: event.allLocations,
      companyId: event.companyId,
    });

    const playlistItems = event.playlists.map((item) => {
      return {
        musicId: item.playlist._id,
        type: 'playlist',
        offset: item.offset,
        duration: item.duration,
      };
    });

    const mixItems = event.mixes.map((item) => {
      return {
        musicId: item.mix._id,
        type: 'mix',
        offset: item.offset,
        duration: item.duration,
      };
    });

    const musicParts = playlistItems.concat(mixItems);

    // Get tags to add/remove
    const modifyTags = event.tags.filter(tag => tag.status === 'add' || tag.status === 'remove');

    // Save data for the API
    const data = {
      name: event.title,
      companyId: event.companyId,
      description: event.description,
      locations: locationData,
      duration: event.duration,
      schedule: event.schedule,
      scheduleOriginTimeZone: event.scheduleOriginTimeZone,
      color: event.color,
      playBaselineMusic: event.playBaselineMusic,
      playBaselineMessageList: event.playBaselineMessageBlocks,
      playBaselineInterrupts: event.playBaselineInterrupts,
      volume: event.volume,
      tags: modifyTags.map((tag) => {
        return {
          _id: tag._id,
          action: tag.status,
        };
      }),
      musicParts,
      messageParts: event.messageBlocks.map((item) => {
        return {
          messageListId: item.messageBlock._id,
          offset: item.offset,
          duration: item.duration,
        };
      }),
      interrupts: event.interruptions.map((item) => {
        return {
          messageId: item.message._id,
          offset: item.offset,
        };
      }),
    };

    if (event._id && event._id.indexOf('new-event') <= -1) {
      data._id = event._id;
    }

    try {
      const eventResponse = event._id.indexOf('new-event') >= 0
        ? await API.Event.create(data)
        : await API.Event.update(data);

      return eventResponse;
    } catch (err) {
      return [{
        type: 'EVENT.ERROR',
        error: err,
      }];
    }
  };
}

/**
 * Update an Event
 */
export function updateEvent(event) {
  const {
    updateRule,
  } = event;

  // if (updateRule) {
  //   // Do not set this parameter on the event
  //   delete event.updateRule;
  // }

  // if (event.date) {
  //   // Do not update the date of the event
  //   delete event.date;
  // }

  const {
    playlists,
    mixes,
    messageBlocks,
    interruptions,
  } = event;

  // get the event start time (depending on if a time was passed in the data)
  const eventStartTime = event.startTime;
  // get the event stop time (depending on if a time was passed in the data)
  const eventStopTime = event.stopTime;

  // Start/stop time for the event was changed. Get new duration.
  event.duration = getDuration(eventStartTime, eventStopTime);

  // Playlists and stations share the music column, so they are fit together
  const musicItems = fitItems({
    items: [
      ...playlists,
      ...mixes,
    ],
    startTime: eventStartTime,
    stopTime: eventStopTime,
  });

  event.playlists = musicItems.filter(item => item.playlist);
  event.mixes = musicItems.filter(item => item.mix);

  event.messageBlocks = fitItems({
    items: messageBlocks,
    startTime: eventStartTime,
    stopTime: eventStopTime,
  });

  event.interruptions = fitInterrupts({
    interrupts: interruptions,
    startTime: eventStartTime,
    stopTime: eventStopTime,
  });

  event.repeatRule = updateRule
    ? updateRepeatRule(event)
    : event.repeatRule;

  event.schedule = event.repeatRule.toString();

  const updatedEvent = new EventModel(event);
  // console.log('event1', event);
  // console.log('event2', updatedEvent);

  return updatedEvent;
}

export default {
  saveEvent,
  updateEvent,
};
